import { UserAvatar } from "@promptlayer/ui";
import { X } from "lucide-react";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import EntityTag from "./EntityTag";

import { Notification } from "@/queries/notifications/useNotifications";

interface NotificationItemProps extends Notification {
  onDismiss?: (id: number) => void;
}

const getPriorityColor = (priority: "low" | "medium" | "high") => {
  switch (priority) {
    case "high":
      return "bg-red-500 ring-red-100 dark:ring-red-900/30";
    case "medium":
      return "bg-orange-500 ring-orange-100 dark:ring-orange-900/30";
    case "low":
      return "bg-blue-500 ring-blue-100 dark:ring-blue-900/30";
    default: {
      const _exhaustiveCheck: never = priority;
      return _exhaustiveCheck;
    }
  }
};

const NotificationItem = ({
  id,
  created_at,
  title,
  link,
  priority,
  metadata,
  triggered_by_email,
  onDismiss,
}: NotificationItemProps) => {
  const [isExiting, setIsExiting] = useState(false);
  const navigate = useNavigate();

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExiting(true);
    setTimeout(() => {
      onDismiss?.(id);
    }, 200);
  };

  const handleClick = () => {
    try {
      // Extract path from full URL for React Router navigation
      const url = new URL(link);
      const path = url.pathname + url.search + url.hash;
      navigate(path);
    } catch {
      // If link is not a valid URL, assume it's already a path
      navigate(link);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`group relative flex cursor-pointer items-start gap-3 border-b border-gray-100/50 px-3 py-3 transition-all duration-200 last:border-b-0 hover:bg-gray-50/70 dark:border-gray-800/50 dark:hover:bg-gray-800/30 ${
        isExiting ? "translate-x-2 opacity-0" : ""
      }`}
    >
      <div className="mt-0.5 flex-shrink-0">
        {triggered_by_email ? (
          <UserAvatar email={triggered_by_email} className="h-5 w-5 text-[10px]" />
        ) : (
          <div className={`mt-1 h-1.5 w-1.5 rounded-full ring-2 ${getPriorityColor(priority)}`} />
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <p
          dangerouslySetInnerHTML={{ __html: title }}
          className="text-[13px]  text-gray-900 dark:text-gray-100"
        />
        <EntityTag metadata={metadata} />
        {/*<p
          dangerouslySetInnerHTML={{ __html: message }}
          className="text-[13px] leading-relaxed text-gray-700 dark:text-gray-300 [&>strong]:font-medium [&>strong]:text-gray-900 dark:[&>strong]:text-gray-100"
        />*/}
        <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500">
          {moment(created_at).fromNow()}
        </p>
      </div>
      <button
        onClick={handleDismiss}
        className="invisible ml-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded transition-all hover:bg-gray-200/70 group-hover:visible dark:hover:bg-gray-700/70"
        aria-label="Dismiss notification"
      >
        <X className="h-3 w-3 text-gray-400 dark:text-gray-500" />
      </button>
    </div>
  );
};

export default NotificationItem;
